<template>
  <BlogPostLayout>
    <template #image>
      <BlogHeader
        :title="postData.title"
        :excerpt="postData.excerpt"
        :image="postData.image"
        :author="postData.author"
        :date="postData.date"
        :updatedAt="postData.updatedAt"
        :categories="postData.categories"
        :readTime="postData.readTime"
      />
    </template>
    <template #content>
      <article class="blog-content" lang="es">

        <section class="intro">
          <p>
            Añadir un <strong>tour virtual a tu Perfil de Empresa de Google</strong> es uno de los recursos SEO local más poderosos y menos utilizados en España y Latinoamérica en 2026. Los negocios con tour virtual en Google Business obtienen en promedio un <strong>42% más de solicitudes de ruta</strong> y aparecen más arriba en el Local Pack. Esta guía te muestra cómo hacerlo en menos de 2 horas.
          </p>
        </section>

        <section>
          <h2>Por qué un tour virtual mejora tu posicionamiento en Google Business</h2>
          <p>
            Google favorece los perfiles que ofrecen una experiencia completa al usuario. Un tour virtual envía señales positivas al algoritmo:
          </p>
          <ul>
            <li><strong>Mayor tiempo en el perfil</strong> — los usuarios pasan 3 veces más tiempo en un perfil con tour virtual, lo que mejora el engagement.</li>
            <li><strong>Contenido visual rico</strong> — Google premia los perfiles con medios de calidad en los resultados de búsqueda local.</li>
            <li><strong>Confianza y credibilidad</strong> — un perfil con tour virtual tranquiliza al cliente potencial y reduce el rebote.</li>
          </ul>
        </section>

        <section>
          <h2>Método 1 — Mediante la app Google Street View (gratis)</h2>

          <h3>Paso 1: Descarga la app Google Street View</h3>
          <p>Disponible en iOS y Android. Asegúrate de estar conectado con la misma cuenta Google que tu Perfil de Empresa.</p>

          <h3>Paso 2: Captura tus panoramas</h3>
          <p>En la aplicación, pulsa "Cámara" y elige el modo Photo Sphere. Gira lentamente siguiendo las guías visuales hasta cubrir la esfera completa. Repite en cada espacio.</p>

          <h3>Paso 3: Publica directamente en Google</h3>
          <p>Una vez capturados y ensamblados los panoramas, pulsa "Publicar". Google los asocia automáticamente a tu ficha según la geolocalización. Plazo de aparición: 24–72 horas.</p>
        </section>

        <section>
          <h2>Método 2 — Mediante Travvir + enlace (recomendado)</h2>
          <p>
            Para un tour virtual más completo (con navegación entre estancias, hotspots y branding), crea primero el tour en Travvir y luego enlázalo desde tu Perfil de Empresa de Google.
          </p>

          <h3>Paso 1: Crea tu tour en Travvir</h3>
          <p>Captura cada espacio con la app Travvir y publica el tour. Copia el enlace compartible.</p>

          <h3>Paso 2: Añade el enlace a tu Perfil de Empresa de Google</h3>
          <p>En el gestor de Google Business Profile, ve a "Información" → "Sitio web" → añade la URL de tu tour Travvir o inclúyela en el campo de descripción del negocio.</p>

          <h3>Paso 3: Sube panoramas 360° como fotos</h3>
          <p>En la sección "Fotos" de tu Perfil de Empresa, puedes subir imágenes panorámicas equirrectangulares. Google las reconoce automáticamente y las muestra en modo 360°.</p>
        </section>

        <section>
          <h2>Buenas prácticas para maximizar el impacto SEO local</h2>
          <ul>
            <li><strong>Incluye el exterior de tu negocio</strong> — los panoramas de la fachada ayudan a los clientes a identificar físicamente el local.</li>
            <li><strong>Cubre todos los espacios clave</strong> — para un restaurante: comedor, barra, terraza. Para un hotel: recepción, habitación tipo, piscina.</li>
            <li><strong>Actualiza regularmente</strong> — un tour reciente (menos de 18 meses) tiene más peso en el algoritmo de Google.</li>
            <li><strong>Pide reseñas tras la visita virtual</strong> — los clientes que han visitado virtualmente dejan reseñas positivas con mayor facilidad.</li>
          </ul>
        </section>

        <section>
          <h2>Preguntas frecuentes</h2>

          <h3>¿Puedo añadir un tour virtual a Google Business sin ser fotógrafo certificado?</h3>
          <p>Sí. Desde 2022, cualquier propietario de un Perfil de Empresa puede añadir sus propias fotos esféricas directamente desde la app Google Street View, sin certificación ni intermediarios.</p>

          <h3>¿Cuánto tarda en aparecer el tour virtual en Google?</h3>
          <p>Generalmente entre 24 y 72 horas. En algunos casos puede tardar hasta 7 días si Google realiza una verificación manual.</p>

          <h3>¿El tour virtual de Google Business mejora el ranking en el Local Pack?</h3>
          <p>Sí, de forma indirecta. Mejora el tasa de engagement del perfil (tiempo de permanencia, clics en "Cómo llegar"), que es uno de los factores de posicionamiento local de Google.</p>
        </section>

        <RelatedPosts
          :currentSlug="postData.slug"
          :currentCategories="postData.categories"
        />
      </article>
    </template>
  </BlogPostLayout>
</template>

<script setup>
import blogPostsData from "~/data/blogPosts.json";

const postData = blogPostsData.find(
  (p) => p.slug === "tour-virtual-google-business",
);

useBlogSeo({
  title: "Cómo Añadir un Tour Virtual a Google Business en 2026 | Travvir",
  description:
    "Tutorial completo para añadir un tour virtual 360° a tu Perfil de Empresa de Google y mejorar tu posicionamiento local. Dos métodos: Google Street View y Travvir.",
  keywords:
    "tour virtual google business, añadir tour virtual google, visita 360 google maps, posicionamiento local tour virtual, google business profile tour virtual",
  slug: "tour-virtual-google-business",
  image: postData.image,
  postData,
  lang: "es",
  faqItems: [
    {
      question: "¿Puedo añadir un tour sin ser fotógrafo certificado?",
      answer: "Sí. Cualquier propietario puede añadir fotos esféricas directamente desde la app Google Street View, sin certificación.",
    },
    {
      question: "¿Cuánto tarda en aparecer el tour en Google?",
      answer: "Generalmente 24–72 horas. Puede tardar hasta 7 días si hay verificación manual.",
    },
    {
      question: "¿Mejora el ranking en el Local Pack?",
      answer: "Sí, de forma indirecta, mejorando el engagement del perfil (tiempo, clics en 'Cómo llegar').",
    },
  ],
});

</script>
